/**
 * Inline Document Uploader — Compact chaos upload component
 *
 * DRR-179: Embeddable upload component for attaching documents inline
 * without navigating to Document Studio. Uses the same chaos pipeline
 * (S3 presigned URL → Document primitive → optional OCR/classification)
 * as ChaosDocumentUploader, but renders as a compact dropzone card.
 *
 * Use cases:
 * - Floor plan upload in SpatialPinningEditor
 * - Photo attachment on incident reports
 * - ID document upload during intake
 * - Signature scan on ceremony forms
 *
 * ARCHITECTURE:
 * - Delegates to useChaosDocumentUpload (HIPAA, S3, audit trail)
 * - Mantine only (DRR-055 dumb frontend)
 * - Domain-agnostic — label and accept types are props
 * - DRR-081: allowlist enforced via accept prop (PDF/PNG/TXT/CSV/MD)
 *
 * Usage:
 * ```tsx
 * <InlineDocumentUploader
 *   label="Floor Plan"
 *   accept="image/png,image/jpeg,application/pdf"
 *   sourceSystem="spatial-pinning-editor"
 *   onUploadComplete={(doc) => setFloorPlanUrl(doc.previewUrl)}
 * />
 * ```
 */

import { useCallback, useState, useRef } from 'react';
import {
  Paper,
  Group,
  Stack,
  Text,
  Progress,
  Badge,
  ActionIcon,
  Image,
  Center,
  Tooltip,
} from '@mantine/core';
import {
  IconUpload,
  IconFile,
  IconCheck,
  IconX,
  IconPhoto,
  IconRefresh,
} from '@tabler/icons-react';
import { useChaosDocumentUpload, type UploadResult } from './hooks/useChaosDocumentUpload';

export interface InlineDocumentUploaderProps {
  /** Label shown in the dropzone (e.g., "Floor Plan", "ID Document") */
  label?: string;
  /** Accepted MIME types (e.g., "image/png,application/pdf") */
  accept?: string;
  /** Source system identifier for audit trail */
  sourceSystem?: string;
  /** Additional metadata to attach to the document */
  metadata?: Record<string, unknown>;
  /** Callback when upload completes successfully */
  onUploadComplete?: (result: InlineUploadResult) => void;
  /** Callback when upload fails */
  onUploadError?: (error: Error) => void;
  /** Show image preview after upload (for images only) */
  showPreview?: boolean;
  /** Max height for the component */
  maxHeight?: number;
  /** Current preview URL (for pre-populated state) */
  currentPreviewUrl?: string | null;
}

export interface InlineUploadResult {
  documentId: string;
  previewUrl: string | null;
  fileName: string;
  fileType: string;
}

export function InlineDocumentUploader({
  label = 'Document',
  accept = 'image/png,image/jpeg,application/pdf',
  sourceSystem = 'inline-uploader',
  metadata,
  onUploadComplete,
  onUploadError,
  showPreview = true,
  maxHeight = 200,
  currentPreviewUrl,
}: InlineDocumentUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { upload, uploadProgress, isUploading, error } = useChaosDocumentUpload();

  const [uploadedFile, setUploadedFile] = useState<{
    name: string;
    previewUrl: string | null;
    documentId: string | null;
  } | null>(null);

  const [dragOver, setDragOver] = useState(false);

  const progress = uploadProgress.length > 0
    ? uploadProgress.reduce((sum, p) => sum + p.progress, 0) / uploadProgress.length
    : 0;

  const handleFile = useCallback(
    async (file: File) => {
      // Local preview for images
      const isImage = file.type.startsWith('image/');
      const previewUrl = isImage ? URL.createObjectURL(file) : null;

      setUploadedFile({
        name: file.name,
        previewUrl,
        documentId: null,
      });

      try {
        const result: UploadResult = await upload(file, sourceSystem, metadata as Record<string, string>);

        setUploadedFile((prev) => prev ? { ...prev, documentId: result.document.documentId } : null);

        onUploadComplete?.({
          documentId: result.document.documentId,
          previewUrl,
          fileName: file.name,
          fileType: file.type,
        });
      } catch (err) {
        const uploadError = err instanceof Error ? err : new Error('Upload failed');
        onUploadError?.(uploadError);
        setUploadedFile(null);
      }
    },
    [upload, sourceSystem, metadata, onUploadComplete, onUploadError],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleClear = useCallback(() => {
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const previewUrl = uploadedFile?.previewUrl ?? currentPreviewUrl;
  const hasFile = uploadedFile !== null || currentPreviewUrl !== null;

  return (
    <Paper
      withBorder
      radius="md"
      p="xs"
      style={{
        cursor: isUploading ? 'wait' : 'pointer',
        borderStyle: dragOver ? 'solid' : hasFile ? 'solid' : 'dashed',
        borderColor: dragOver
          ? 'var(--mantine-color-blue-5)'
          : error
            ? 'var(--mantine-color-red-5)'
            : hasFile
              ? 'var(--mantine-color-green-5)'
              : 'var(--mantine-color-default-border)',
        borderWidth: dragOver ? 2 : 1,
        maxHeight,
        overflow: 'hidden',
        transition: 'border-color 150ms ease',
      }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={!hasFile ? handleClick : undefined}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        style={{ display: 'none' }}
      />

      {/* Uploading state */}
      {isUploading && (
        <Stack gap={4} align="center" py="xs">
          <Text size="xs" c="dimmed">Uploading {uploadedFile?.name}...</Text>
          <Progress value={progress} size="sm" w="100%" animated />
        </Stack>
      )}

      {/* Error state */}
      {error && !isUploading && (
        <Group gap="xs" justify="center" py="xs">
          <IconX size={16} color="var(--mantine-color-red-6)" />
          <Text size="xs" c="red">Upload failed</Text>
          <ActionIcon size="xs" variant="subtle" onClick={handleClick}>
            <IconRefresh size={12} />
          </ActionIcon>
        </Group>
      )}

      {/* Uploaded state with preview */}
      {hasFile && !isUploading && !error && (
        <Stack gap={4}>
          {showPreview && previewUrl && (
            <Center>
              <Image
                src={previewUrl}
                alt={label}
                mah={maxHeight - 40}
                fit="contain"
                radius="sm"
              />
            </Center>
          )}
          <Group gap="xs" justify="space-between">
            <Group gap={4}>
              <IconCheck size={14} color="var(--mantine-color-green-6)" />
              <Text size="xs" fw={500} lineClamp={1}>
                {uploadedFile?.name ?? label}
              </Text>
              {uploadedFile?.documentId && (
                <Badge size="xs" variant="light" color="green">saved</Badge>
              )}
            </Group>
            <Group gap={4}>
              <Tooltip label={`Replace ${label.toLowerCase()}`}>
                <ActionIcon size="xs" variant="subtle" onClick={handleClick}>
                  <IconRefresh size={12} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Remove">
                <ActionIcon size="xs" variant="subtle" color="red" onClick={handleClear}>
                  <IconX size={12} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
        </Stack>
      )}

      {/* Empty state — dropzone */}
      {!hasFile && !isUploading && !error && (
        <Center py="sm">
          <Stack gap={4} align="center">
            <IconPhoto size={24} color="var(--mantine-color-dimmed)" />
            <Text size="xs" c="dimmed" ta="center">
              Drop {label.toLowerCase()} here or click to browse
            </Text>
            <Text size="xs" c="dimmed">
              {accept.split(',').map((t) => t.split('/')[1]).join(', ').toUpperCase()}
            </Text>
          </Stack>
        </Center>
      )}
    </Paper>
  );
}
